import { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../../api/axios';
import { Link } from 'react-router-dom';
import { Box, Typography, Button, TextField, Grid, Select, MenuItem, FormControl, FormHelperText, InputLabel, Checkbox, IconButton, InputAdornment, ListItemText, Autocomplete, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ReCAPTCHA from 'react-google-recaptcha';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { Visibility, VisibilityOff, Send, CheckCircle, Cancel } from '@mui/icons-material';
import { notifySuccess, notifyError } from '../../../components/common/toast/ToastFunc';
import usStatesList from '../../../assests/data/usStatesList';
import Image from "../../../assests/images/signup.jpg";

const user_data = {
  first_name: '',
  last_name: '',
  email_id: '',
  mobile_no: '',
  dob: dayjs(),
  address: '',
  city: '',
  state: '',
  zip_code: '',
  id_type: '',
  id_number: '',
  account_types: [],
  role: '',
  master_password: '',
  show_master_password: false,
  password: '',
  confirm_password: '',
  show_password: false,
  show_confirm_password: false,
};

const SignUp = () => {

  const theme = useTheme();
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const location = useLocation();
  const isInternalSignUp = location.pathname.includes('internal-sign-up');

  const roles = isInternalSignUp ? ['Admin'] : ['Customer', 'Merchant', 'Employee', 'Manager'];

  const [formData, setFormData] = useState(user_data);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [layout, setLayout] = useState('default');
  const [currentInput, setCurrentInput] = useState('');
  const keyboardRef = useRef();

  const onKeyboardChange = (input) => {
    setFormData({ ...formData, [currentInput]: input });
  };

  const toggleKeyboard = () => {
    setKeyboardVisible(!keyboardVisible);
    setFormData(user_data);
    setErrors({});
  };

  const handleFocus = (inputName) => {
    setCurrentInput(inputName);
    if (keyboardRef.current) {
      keyboardRef.current.setInput(formData[inputName]);
    }
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const buttonTheme = useMemo(() => ({
    mt: 1,
    color: 'white',
    backgroundColor: theme.mainColor,
    fontSize: 18,
    fontWeight: 'bold',
    borderRadius: 2,
    height: "45px",
    '&:hover': {
      backgroundColor: '#b30000',
      color: 'white',
      fontSize: 18,
      fontWeight: 'bold',
      borderRadius: 2,
    },
  }), [theme.mainColor]);

  const textFieldTheme = useMemo(() => ({
    '& label.Mui-focused': {
      color: theme.mainColor,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'grey',
        borderRadius: '7px',
      },
      '&:hover fieldset': {
        borderColor: theme.mainColor,
      },
      '&.Mui-focused fieldset': {
        border: '1px solid #990000',
      },
    },
  }), [theme.mainColor]);

  const selectTheme = useMemo(() => ({
    '& .MuiInputLabel-root': {
      color: 'grey',
      '&.Mui-focused': {
        color: theme.mainColor,
      },
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'grey',
        borderRadius: '8px',
      },
      '&:hover fieldset': {
        borderColor: theme.mainColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.mainColor,
      },
    },
  }), [theme.mainColor]);

  const getSizeFromRole = useCallback(() => {
    if (["Customer", "Merchant"].includes(formData["role"])) return 4;
    if (["Employee", "Manager"].includes(formData["role"])) return 6;
    if (isInternalSignUp) return 4;
    else return 6;
  }, [formData, isInternalSignUp]);

  const checkPasswordCriteria = useCallback((password) => {
    const lengthCheck = password.length >= 10;
    const uppercaseCheck = /[A-Z]/.test(password);
    const lowercaseCheck = /[a-z]/.test(password);
    const numberCheck = /\d/.test(password);
    const symbolCheck = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return lengthCheck && uppercaseCheck && lowercaseCheck && numberCheck && symbolCheck;
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case 'first_name':
      case 'last_name':
      case 'city':
      case 'address':
      case 'state':
      case 'id_number':
        return value.trim().length > 0 ? '' : 'This field is required';
      case 'email_id':
        return /\S+@\S+\.\S+/.test(value) ? '' : 'Invalid email format';
      case 'mobile_no':
        return /^\d{10}$/.test(value) ? '' : 'Invalid mobile number';
      case 'zip_code':
        return /^\d{5}$/.test(value) ? '' : 'Invalid zip code';
      case 'dob':
        return dayjs().diff(dayjs(value)) <= 0 ? 'Invalid date' : '';
      case 'id_type':
        return ['Passport', 'License', 'SSN'].includes(value) ? '' : 'Invalid ID type';
      case 'account_types':
        if (["Customer", "Merchant"].includes(formData["role"]))
          return value.length > 0 ? '' : 'At least one account type is required';
        return '';
      case 'role':
        return roles.includes(value) ? '' : 'Please select role';
      case 'master_password':
        if (isInternalSignUp && value.trim().length === 0) {
          return 'This field is required';
        }
        return '';
      case 'password':
        if (value.trim().length === 0) {
          return 'This field is required';
        }
        return checkPasswordCriteria(value.trim()) ? '' : 'Password criteria does not match';
      case 'confirm_password':
        if (formData.password.length > 0) {
          return value === formData.password ? '' : 'Password does not match';
        }
        if (value.trim().length === 0) {
          return 'This field is required';
        }
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let newErrors = {};
      Object.keys(formData).forEach((key) => {
        const error = validateField(key, formData[key]);
        if (error) {
          newErrors[key] = error;
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      const captcha_token = captchaRef.current.getValue();
      captchaRef.current.reset();

      if (!captcha_token) {
        notifyError("Invalid Captcha");
        return;
      }

      setLoading(true);
      setFormData(prev => ({ ...prev, dob: dayjs(prev["dob"]).format('MM/DD/YYYY') }))
      const response = await axiosInstance.post('/auth/signup', {
        ...formData,
        dob: dayjs(formData["dob"]).format('MM/DD/YYYY'),
        captcha_token
      });
      notifySuccess(response?.data?.message || "Success");
      navigate('/login');
    } catch (error) {
      notifyError(error.message || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <Grid container spacing={2} sx={{ display: "flex", alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 65px)' }}>
      <Grid item xs={12} md={4} lg={4}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: "100%",
            height: "100%",
            mt: "65px",
            m: 2
          }}>
          <Typography sx={{ fontSize: "30px", color: theme.mainColor, fontWeight: "500" }}>
            Create Bank Account From Anywhere
          </Typography>
          <Box
            component="img"
            sx={{
              height: 500,
              width: 500,
              mt: 3
            }}
            alt="Sign Up Image"
            src={Image}
          />
        </Box>
      </Grid>

      <Grid item xs={12} md={8} lg={8}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            maxWidth: 785,
            p: 2,
          }}
        >
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ color: theme.mainColor }}>
              Get started absolutely free.
            </Typography>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="First Name"
                variant="outlined"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                onFocus={() => handleFocus('first_name')}
                sx={{ ...textFieldTheme }}
                error={!!errors.first_name}
                helperText={errors.first_name}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Last Name"
                variant="outlined"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                onFocus={() => handleFocus('last_name')}
                sx={{ ...textFieldTheme }}
                error={!!errors.last_name}
                helperText={errors.last_name}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Email ID"
                variant="outlined"
                name="email_id"
                value={formData.email_id}
                onChange={handleInputChange}
                onFocus={() => handleFocus('email_id')}
                sx={{ ...textFieldTheme }}
                error={!!errors.email_id}
                helperText={errors.email_id}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Mobile No"
                variant="outlined"
                name="mobile_no"
                value={formData.mobile_no}
                onFocus={() => handleFocus('mobile_no')}
                onChange={handleInputChange}
                sx={{ ...textFieldTheme }}
                error={!!errors.mobile_no}
                helperText={errors.mobile_no}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ mt: -1 }}>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Date of Birth"
                      onChange={(newDate) => setFormData(prev => ({ ...prev, dob: newDate }))}
                    />
                  </DemoContainer>
                </LocalizationProvider>
                <FormHelperText error={!!errors.dob}>{errors.dob}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Address"
                variant="outlined"
                name="address"
                value={formData.address}
                onFocus={() => handleFocus('address')}
                onChange={handleInputChange}
                sx={{ ...textFieldTheme }}
                error={!!errors.address}
                helperText={errors.address}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                name="city"
                value={formData.city}
                onFocus={() => handleFocus('city')}
                onChange={handleInputChange}
                sx={{ ...textFieldTheme }}
                error={!!errors.city}
                helperText={errors.city}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  options={usStatesList}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      variant="outlined"
                      name="state"
                      sx={{
                        ...textFieldTheme
                      }}
                    />
                  )}
                  value={usStatesList.find(state => state.name === formData.state) || null}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, state: newValue ? newValue.name : '' });
                  }}
                  ListboxProps={{ style: { maxHeight: '200px', overflow: 'auto', borderRadius: '8px' } }}
                />
                <FormHelperText error={!!errors.state}>{errors.state}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Zip Code"
                variant="outlined"
                name="zip_code"
                value={formData.zip_code}
                onFocus={() => handleFocus('zip_code')}
                onChange={handleInputChange}
                sx={{ ...textFieldTheme }}
                error={!!errors.zip_code}
                helperText={errors.zip_code}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth sx={{ ...selectTheme }}>
                <InputLabel>ID Type</InputLabel>
                <Select
                  label="ID Type"
                  name="id_type"
                  value={formData.id_type}
                  onChange={handleInputChange}
                >
                  <MenuItem value="Passport">Passport</MenuItem>
                  <MenuItem value="License">License</MenuItem>
                  <MenuItem value="SSN">SSN</MenuItem>
                </Select>
                <FormHelperText error={!!errors.id_type}>{errors.id_type}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ID Number"
                variant="outlined"
                name="id_number"
                value={formData.id_number}
                onFocus={() => handleFocus('id_number')}
                onChange={handleInputChange}
                sx={{ ...textFieldTheme }}
                error={!!errors.id_number}
                helperText={errors.id_number}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth sx={{ ...selectTheme }}>
                <InputLabel error={!!errors.role}>Role</InputLabel>
                <Select
                  label="Role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  {roles.map((role, index) => (
                    <MenuItem key={index} value={role}>{role}</MenuItem>
                  ))}
                </Select>
                <FormHelperText error={!!errors.role}>{errors.role}</FormHelperText>
              </FormControl>
            </Grid>
            {isInternalSignUp ?
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type={formData.show_master_password ? 'text' : 'password'}
                  label="Master Password"
                  variant="outlined"
                  name="master_password"
                  value={formData.master_password}
                  onFocus={() => handleFocus('master_password')}
                  onChange={handleInputChange}
                  sx={{ ...textFieldTheme }}
                  error={!!errors.master_password}
                  helperText={errors.master_password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setFormData(prev => ({ ...prev, show_master_password: !prev.show_master_password }))}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {formData.show_master_password ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid> :
              (
                ["Customer", "Merchant"].includes(formData["role"]) &&
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth sx={{ ...selectTheme }}>
                    <InputLabel error={!!errors.account_types}>Type of Account</InputLabel>
                    <Select
                      multiple
                      required
                      label="Type of Account"
                      value={formData.account_types}
                      renderValue={(selected) => selected.join(', ')}
                      onChange={(event) => setFormData({ ...formData, account_types: event.target.value })}
                      error={!!errors.account_types}
                    >
                      {['Checking', 'Savings'].map((type) => (
                        <MenuItem key={type} value={type}>
                          <Checkbox checked={formData.account_types.indexOf(type) > -1} />
                          <ListItemText primary={type} />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error={!!errors.account_types}>{errors.account_types}</FormHelperText>
                  </FormControl>
                </Grid>
              )
            }
            <Grid item xs={12} sm={getSizeFromRole()}>
              <TextField
                fullWidth
                type={formData.show_password ? 'text' : 'password'}
                label="Create Password"
                variant="outlined"
                name="password"
                value={formData.password}
                onFocus={() => handleFocus('password')}
                onChange={handleInputChange}
                sx={{ ...textFieldTheme }}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setFormData(prev => ({ ...prev, show_password: !prev.show_password }))}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {formData.show_password ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={getSizeFromRole()}>
              <TextField
                fullWidth
                type={formData.show_confirm_password ? 'text' : 'password'}
                label="Re-enter Password"
                variant="outlined"
                name="confirm_password"
                value={formData.confirm_password}
                onFocus={() => handleFocus('confirm_password')}
                onChange={handleInputChange}
                sx={{ ...textFieldTheme }}
                error={!!errors.confirm_password}
                helperText={errors.confirm_password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setFormData(prev => ({ ...prev, show_confirm_password: !prev.show_confirm_password }))}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {formData.show_confirm_password ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              {formData.password && (
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    fontSize: "16px",
                    color: checkPasswordCriteria(formData.password) ? "green" : "red",
                  }}
                >
                  {checkPasswordCriteria(formData.password) ?
                    <CheckCircle sx={{ fontSize: 20, mr: 1, color: "green" }} /> :
                    <Cancel sx={{ fontSize: 20, mr: 1, color: "red" }} />}
                  At least 10 characters long, a combination of uppercase letters, lowercase letters, numbers, and symbols.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sx={{ my: -1.5 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Button variant="contained" onClick={toggleKeyboard} sx={{
                  mb: 2,
                  color: 'white',
                  backgroundColor: theme.mainColor,
                  fontSize: 14,
                  fontWeight: 'bold',
                  borderRadius: 2,
                  height: "40px",
                  '&:hover': {
                    backgroundColor: '#b30000',
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 'bold',
                    borderRadius: 2,
                  },
                }}>
                  {keyboardVisible ? 'Hide Virtual Keyboard' : 'Show Virtual Keyboard'}
                </Button>

                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_SITE_KEY}
                  ref={captchaRef}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sx={{ mb: 2 }}>
              {keyboardVisible && (
                <Keyboard
                  layoutName={layout}
                  keyboardRef={r => (keyboardRef.current = r)}
                  onChange={onKeyboardChange}
                  onKeyPress={button => {
                    const newLayoutName = layout === 'default' ? 'shift' : 'default';
                    if (button === '{shift}' || button === '{lock}') {
                      setLayout(newLayoutName);
                    }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <LoadingButton
                onClick={handleSubmit}
                loading={loading}
                loadingPosition="start"
                startIcon={<Send />}
                variant="contained"
                sx={{ ...buttonTheme, mt: -1.5 }}
                fullWidth
              >
                Sign Up
              </LoadingButton>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Link to="/login" style={{ textDecoration: 'none', color: theme.mainColor }}>
                <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                  Already a customer? Login
                </Typography>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SignUp;
